import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { RefreshError } from "../components/PullToRefresh";
import { useI18n } from "./i18n";
import { ignoreOverviewAbort, overviewAccessLost, overviewFailureKey, overviewProfileLost, useOverviewRead } from "./useOverviewRead";

type RefreshFamily = { read: () => Promise<() => void>; clear: () => void };
type RefreshOptions = {
  enabled?: boolean; auto?: boolean; onProfileLost?: () => void;
  /** Passive inbox errors belong to the primary list; manual pulls still report every family. */
  primaryFamily?: { index: number; loadError: string };
};

/** Nested owner controls cancel a containing list read before dispatching a command. */
export const RefreshCancellation = createContext<() => void>(() => {});
export function useCancelRefresh() { return useContext(RefreshCancellation); }

/** Each list owner names its complete read set and its access-loss cleanup.
 * Stage successful results until every family settles and ownership is checked. */
export function refreshFamily<T>(read: () => Promise<T>, commit: (value: T) => void, clear: () => void): RefreshFamily {
  return { read: async () => { const value = await read(); return () => commit(value); }, clear };
}

export function requireRefreshArray<T>(value: T[]): T[] {
  if (!Array.isArray(value)) throw new Error("Missing list projection.");
  return value;
}

/** List and financial owners share settlement, while retaining their datasets and command feedback.
 * No projection is committed before freshness is checked; profile refusal discards even successful siblings. */
export async function settleRefreshFamilies(tasks: RefreshFamily[], assertCurrent: () => void, onProfileLost?: () => void) {
  const results = await Promise.allSettled(tasks.map(task => task.read()));
  assertCurrent();
  const failures = results.flatMap(result => result.status === "rejected" ? [result.reason as unknown] : []);
  const profileLost = failures.some(overviewProfileLost);
  if (profileLost) { onProfileLost?.(); tasks.forEach(task => task.clear()); }
  else results.forEach((result, index) => {
    if (result.status === "fulfilled") result.value();
    else if (overviewAccessLost(result.reason)) tasks[index].clear();
  });
  return {
    profileLost,
    failureKey: failures.length ? overviewFailureKey(failures) : null,
    familyErrors: results.map(result => result.status === "rejected" ? overviewFailureKey([result.reason]) : null),
  };
}

/** Shared by changing list owners, never by commands or form persistence.
 * Polls yield; a foreground read supersedes prior work. Partial failures retain
 * readable families, while refused profile access clears the whole projection. */
export function useRefreshOwner(identity: string, families: (signal: AbortSignal) => RefreshFamily[], options: RefreshOptions = {}) {
  const { enabled = true, auto = true } = options;
  const { t } = useI18n();
  const reads = useOverviewRead(identity);
  const latest = useRef(families);
  latest.current = families;
  const clearProfile = useRef(options.onProfileLost);
  clearProfile.current = options.onProfileLost;
  const primaryFamily = useRef(options.primaryFamily);
  primaryFamily.current = options.primaryFamily;
  const [state, setState] = useState<{ identity: string; loaded: boolean; error: string | null }>({ identity, loaded: false, error: null });
  const read = useCallback((signal?: AbortSignal, background = false, manual = false) => reads.run(async (readSignal, assertCurrent) => {
    const tasks = latest.current(readSignal);
    const { profileLost, failureKey: key, familyErrors } = await settleRefreshFamilies(tasks, assertCurrent, clearProfile.current);
    const message = key ? t(key === "refresh.overviewPartial" && !manual ? "refresh.overviewLoadError" : key) : null;
    let loadError = message;
    const primary = primaryFamily.current;
    if (primary && !profileLost) {
      const familyError = familyErrors[primary.index];
      if (familyError) {
        loadError = familyError !== "refresh.overviewPartial" ? t(familyError) : primary.loadError;
      } else loadError = null;
    }
    setState({ identity, loaded: true, error: manual ? null : loadError });
    if (manual && message) throw new RefreshError(message);
  }, { signal, background }), [identity, reads.run, t]);
  const reload = useCallback((background = false) => read(undefined, background).catch(ignoreOverviewAbort), [read]);
  useEffect(() => { if (enabled && auto) void reload(); }, [identity, enabled, auto]);
  return {
    loaded: state.identity === identity && state.loaded,
    error: state.identity === identity ? state.error : null,
    reading: reads.reading,
    cancel: reads.cancel,
    reload,
    onRefresh: (signal: AbortSignal) => read(signal, false, true),
  };
}
